import { useEffect, useState } from "react";

const today = () => new Date().toISOString().slice(0, 10);

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

function NewMeetingForm({ chapterId, onCreated }) {
  const [form, setForm] = useState({ title: "", meeting_date: today(), location: "", agenda: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    const res = await api(`/api/chapters/${chapterId}/meetings`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    if (res.ok) onCreated((await res.json()).id);
    else setError((await res.json().catch(() => ({}))).error || "Could not create meeting.");
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Title
          <input name="title" required value={form.title} onChange={set("title")} placeholder="Board meeting" />
        </label>
        <label className="field">
          Date
          <input type="date" name="meeting_date" required value={form.meeting_date} onChange={set("meeting_date")} />
        </label>
        <label className="field">
          Location
          <input name="location" value={form.location} onChange={set("location")} placeholder="Teams / venue" />
        </label>
        <button className="btn-primary" style={{ width: "auto" }} type="submit">
          Create meeting
        </button>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

function EditableText({ label, value, placeholder, onSave }) {
  const [text, setText] = useState(value || "");
  const [saved, setSaved] = useState(false);
  useEffect(() => setText(value || ""), [value]);

  async function save() {
    await onSave(text);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  return (
    <div className="section">
      <div className="section-head">
        <h3>{label}</h3>
      </div>
      <textarea name={label.toLowerCase()} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} />
      <div className="field-actions">
        <button className="btn-ghost" onClick={save} type="button">
          Save {label.toLowerCase()}
        </button>
        {saved && <span className="saved">Saved</span>}
      </div>
    </div>
  );
}

function Decisions({ meeting, canManage, reload }) {
  const [text, setText] = useState("");

  async function add(e) {
    e.preventDefault();
    if (!text.trim()) return;
    await api(`/api/meetings/${meeting.id}/decisions`, { method: "POST", body: JSON.stringify({ text }) });
    setText("");
    reload();
  }
  async function remove(id) {
    await api(`/api/decisions/${id}`, { method: "DELETE" });
    reload();
  }

  return (
    <div className="section">
      <div className="section-head">
        <h3>Decisions</h3>
      </div>
      {meeting.decisions.length ? (
        <table className="tbl">
          <tbody>
            {meeting.decisions.map((d) => (
              <tr key={d.id}>
                <td>{d.text}</td>
                {canManage && (
                  <td className="row-actions">
                    <button className="btn-danger" onClick={() => remove(d.id)} type="button">
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No decisions recorded.</p>
      )}
      {canManage && (
        <form className="field-actions" onSubmit={add} style={{ marginTop: 10 }}>
          <input name="decision" value={text} onChange={(e) => setText(e.target.value)} placeholder="Record a decision" style={{ flex: 1 }} />
          <button className="btn-ghost" type="submit">
            Add
          </button>
        </form>
      )}
    </div>
  );
}

function ActionItems({ meeting, canManage, reload }) {
  const [form, setForm] = useState({ description: "", owner_name: "", due_on: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function add(e) {
    e.preventDefault();
    if (!form.description.trim()) return;
    await api(`/api/meetings/${meeting.id}/action-items`, { method: "POST", body: JSON.stringify(form) });
    setForm({ description: "", owner_name: "", due_on: "" });
    reload();
  }
  async function toggle(item) {
    await api(`/api/action-items/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: item.status === "done" ? "open" : "done" }),
    });
    reload();
  }
  async function remove(id) {
    await api(`/api/action-items/${id}`, { method: "DELETE" });
    reload();
  }

  return (
    <div className="section">
      <div className="section-head">
        <h3>Action items</h3>
      </div>
      {meeting.action_items.length ? (
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 32 }} />
              <th>Action</th>
              <th>Owner</th>
              <th>Due</th>
              {canManage && <th />}
            </tr>
          </thead>
          <tbody>
            {meeting.action_items.map((a) => (
              <tr key={a.id}>
                <td>
                  <input type="checkbox" name={`action-${a.id}`} aria-label="Mark done" checked={a.status === "done"} disabled={!canManage} onChange={() => toggle(a)} style={{ width: "auto" }} />
                </td>
                <td className={a.status === "done" ? "done-text" : ""}>{a.description}</td>
                <td className="muted">{a.owner_name || "—"}</td>
                <td className="muted">{a.due_on || "—"}</td>
                {canManage && (
                  <td className="row-actions">
                    <button className="btn-danger" onClick={() => remove(a.id)} type="button">
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No action items.</p>
      )}
      {canManage && (
        <form className="form-card" onSubmit={add} style={{ marginTop: 10 }}>
          <div className="form-grid">
            <label className="field">
              Action
              <input name="description" required value={form.description} onChange={set("description")} placeholder="What needs doing" />
            </label>
            <label className="field">
              Owner
              <input name="owner_name" value={form.owner_name} onChange={set("owner_name")} placeholder="Who" />
            </label>
            <label className="field">
              Due
              <input type="date" name="due_on" value={form.due_on} onChange={set("due_on")} />
            </label>
            <button className="btn-ghost" type="submit">
              Add action
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function MeetingDetail({ meetingId, canManage, onBack, onDeleted }) {
  const [meeting, setMeeting] = useState(null);

  function load() {
    api(`/api/meetings/${meetingId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then(setMeeting);
  }
  useEffect(load, [meetingId]);

  async function patch(body) {
    await api(`/api/meetings/${meetingId}`, { method: "PATCH", body: JSON.stringify(body) });
    load();
  }
  async function del() {
    if (!window.confirm("Delete this meeting and its decisions/actions?")) return;
    await api(`/api/meetings/${meetingId}`, { method: "DELETE" });
    onDeleted();
  }

  if (meeting === null) return <p className="muted">Loading meeting…</p>;

  return (
    <>
      <div className="section-head">
        <button className="link-btn" onClick={onBack} type="button">
          ← All meetings
        </button>
        {canManage && (
          <button className="btn-danger" onClick={del} type="button">
            Delete meeting
          </button>
        )}
      </div>
      <h2 style={{ margin: "6px 0 2px" }}>{meeting.title}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {meeting.meeting_date}
        {meeting.location ? ` · ${meeting.location}` : ""}
      </p>

      {canManage ? (
        <>
          <EditableText label="Agenda" value={meeting.agenda} placeholder="Agenda…" onSave={(v) => patch({ agenda: v })} />
          <EditableText label="Minutes" value={meeting.minutes} placeholder="Minutes…" onSave={(v) => patch({ minutes: v })} />
        </>
      ) : (
        <>
          <div className="section">
            <div className="section-head"><h3>Agenda</h3></div>
            <p style={{ whiteSpace: "pre-wrap" }}>{meeting.agenda || <span className="empty">No agenda.</span>}</p>
          </div>
          <div className="section">
            <div className="section-head"><h3>Minutes</h3></div>
            <p style={{ whiteSpace: "pre-wrap" }}>{meeting.minutes || <span className="empty">No minutes.</span>}</p>
          </div>
        </>
      )}

      <Decisions meeting={meeting} canManage={canManage} reload={load} />
      <ActionItems meeting={meeting} canManage={canManage} reload={load} />
    </>
  );
}

export function Meetings({ chapterId, canManage }) {
  const [meetings, setMeetings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  function load() {
    api(`/api/chapters/${chapterId}/meetings`)
      .then((res) => (res.ok ? res.json() : { meetings: [] }))
      .then((data) => setMeetings(data.meetings));
  }
  useEffect(load, [chapterId]);

  if (selected) {
    return (
      <MeetingDetail
        meetingId={selected}
        canManage={canManage}
        onBack={() => { setSelected(null); load(); }}
        onDeleted={() => { setSelected(null); load(); }}
      />
    );
  }

  if (meetings === null) return <p className="muted">Loading meetings…</p>;

  return (
    <>
      <div className="section-head">
        <h3>{meetings.length} meeting{meetings.length === 1 ? "" : "s"}</h3>
        {canManage && (
          <button className="btn-primary" style={{ width: "auto" }} onClick={() => setCreating(!creating)} type="button">
            {creating ? "Close" : "New meeting"}
          </button>
        )}
      </div>
      {creating && (
        <NewMeetingForm chapterId={chapterId} onCreated={(id) => { setCreating(false); setSelected(id); }} />
      )}
      <div style={{ marginTop: 14 }}>
        {meetings.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Location</th>
                <th>Decisions</th>
                <th>Open actions</th>
              </tr>
            </thead>
            <tbody>
              {meetings.map((m) => (
                <tr key={m.id} style={{ cursor: "pointer" }} onClick={() => setSelected(m.id)}>
                  <td className="muted">{m.meeting_date}</td>
                  <td><strong>{m.title}</strong></td>
                  <td className="muted">{m.location || "—"}</td>
                  <td>{m.decision_count}</td>
                  <td>{m.open_actions > 0 ? <span className="pill">{m.open_actions} open</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty">No meetings yet — create one to record agenda, minutes, decisions and actions.</p>
        )}
      </div>
    </>
  );
}
